import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, ExternalLink, Copy, Trash2, Pencil } from "lucide-react";

export default function ClientVault({ clientId }) {
  const [systems, setSystems] = useState([]);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [revealed, setRevealed] = useState({});
  const [revealLoading, setRevealLoading] = useState(null);

  const load = () => {
    api.get(`/clients/${clientId}/vault`)
      .then((r) => { setSystems(r.data.systems); setErr(null); })
      .catch((e) => setErr(e.response?.data?.detail || "Şifre Kasası için yetkiniz yok"));
  };
  useEffect(() => { load(); }, [clientId]);

  if (err) return <Card className="p-10 text-center text-muted-foreground">{err}</Card>;

  const openEdit = (sys) => { setEditing(sys); setForm({}); setShowKeys({}); };
  const toggleShow = (key) => setShowKeys((s) => ({ ...s, [key]: !s[key] }));

  const save = async () => {
    const payload = {};
    editing.keys.forEach(({ key }) => { if (form[key]) payload[key] = form[key]; });
    if (!Object.keys(payload).length) return toast.error("En az bir alan girin");
    try {
      await api.put(`/clients/${clientId}/vault/${editing.system}`, payload);
      toast.success("Bilgiler şifrelenerek kaydedildi");
      setEditing(null); setForm({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const del = async (sys) => {
    try {
      await api.delete(`/clients/${clientId}/vault/${sys.system}`);
      toast.success("Bilgiler silindi");
      setRevealed((r) => { const n = { ...r }; delete n[sys.system]; return n; });
      load();
    } catch { toast.error("Silinemedi"); }
  };

  const toggleReveal = async (sys) => {
    if (revealed[sys.system]) {
      setRevealed((r) => { const n = { ...r }; delete n[sys.system]; return n; });
      return;
    }
    setRevealLoading(sys.system);
    try {
      const { data } = await api.get(`/clients/${clientId}/vault/${sys.system}/reveal`);
      setRevealed((r) => ({ ...r, [sys.system]: data.values }));
    } catch (e) { toast.error(e.response?.data?.detail || "Görüntülenemedi"); }
    finally { setRevealLoading(null); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Kopyalandı"); };
  const go = (sys) => window.open(sys.url, "_blank", "noopener,noreferrer");

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Bilgiler şifrelenerek saklanır. Görüntülediğinizde işlem kaydına (audit log) düşer. "Git" butonu ilgili sitenin giriş
        sayfasını yeni sekmede açar; siteler otomatik doldurulamaz, alanları kopyalayıp yapıştırmanız gerekir.
      </p>

      {systems.map((sys) => {
        const saved = sys.status === "Kayıtlı";
        const rev = revealed[sys.system];
        return (
          <Card key={sys.system} className="p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <KeyRound size={16} className={saved ? "text-emerald-600" : "text-muted-foreground"} />
                {sys.label}
                <Badge data-testid={`vault-status-${sys.system}`} variant={saved ? "default" : "secondary"}>{sys.status}</Badge>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Button size="sm" variant="outline" onClick={() => go(sys)}>
                  <ExternalLink size={14} className="mr-1.5" />Git
                </Button>
                {saved && (
                  <Button
                    size="sm" variant="outline" data-testid={`vault-reveal-${sys.system}`}
                    onClick={() => toggleReveal(sys)} disabled={revealLoading === sys.system}
                  >
                    {rev ? <EyeOff size={14} className="mr-1.5" /> : <Eye size={14} className="mr-1.5" />}
                    {rev ? "Gizle" : "Göster"}
                  </Button>
                )}
                <Button size="sm" variant="outline" data-testid={`vault-edit-${sys.system}`} onClick={() => openEdit(sys)}>
                  <Pencil size={14} className="mr-1.5" />{saved ? "Güncelle" : "Bilgi Gir"}
                </Button>
                {saved && (
                  <Button size="sm" variant="ghost" data-testid={`vault-del-${sys.system}`} onClick={() => del(sys)}>
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </div>

            {rev && (
              <div className="rounded-lg border border-border bg-secondary/40 p-3 space-y-2">
                {sys.keys.map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    <span className="text-xs text-muted-foreground w-32 shrink-0">{label}</span>
                    <span className="font-mono flex-1 truncate">{rev[key] || "—"}</span>
                    {rev[key] && (
                      <Button size="sm" variant="ghost" onClick={() => copy(rev[key])}>
                        <Copy size={13} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing?.label} Erişim Bilgileri</DialogTitle>
            <DialogDescription className="sr-only">{editing?.label} giriş bilgileri (şifreli saklanır)</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Bilgiler şifrelenerek saklanır. Yalnızca güncellemek istediğiniz alanları doldurun.</p>
          <div className="space-y-3 py-2">
            {editing?.system === "sgk" && (
              <div className="space-y-1">
                <Label className="text-xs">Kullanıcı Adı</Label>
                <div className="flex items-center gap-1.5">
                  <Input
                    data-testid="vault-input-sgk-tckn"
                    value={form.tckn || ""}
                    onChange={(e) => setForm({ ...form, tckn: e.target.value })}
                    autoComplete="off"
                    placeholder="TC Kimlik No"
                  />
                  <span className="text-muted-foreground">-</span>
                  <Input
                    data-testid="vault-input-sgk-dosya_kodu"
                    value={form.dosya_kodu || ""}
                    onChange={(e) => setForm({ ...form, dosya_kodu: e.target.value })}
                    autoComplete="off"
                    className="w-16 shrink-0"
                    placeholder="Kod"
                  />
                </div>
              </div>
            )}
            {editing?.keys
              .filter(({ key }) => !(editing.system === "sgk" && (key === "tckn" || key === "dosya_kodu")))
              .map(({ key, label, placeholder }) => (
                <div key={key} className="space-y-1">
                  <Label className="text-xs">{label}</Label>
                  <div className="relative">
                    <Input
                      data-testid={`vault-input-${editing.system}-${key}`}
                      type={key === "tckn" || showKeys[key] ? "text" : "password"}
                      placeholder={placeholder || undefined}
                      value={form[key] || ""}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      autoComplete={key === "tckn" ? "off" : "new-password"}
                      className={key === "tckn" ? "" : "pr-9"}
                    />
                    {key !== "tckn" && (
                      <button
                        type="button"
                        data-testid={`vault-input-toggle-${editing.system}-${key}`}
                        onClick={() => toggleShow(key)}
                        tabIndex={-1}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showKeys[key] ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>
          <DialogFooter><Button data-testid="vault-save" onClick={save}>Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
